/**
 * My work (screen 11) / Batch runs: the operator's list of batches to act on, ordered so the one that needs
 * attention is first. Floor surface — one primary action per row, nothing hover-dependent.
 *
 * Built from the white kit (design-system/MASTER.md), wired to the batch list the API returns. Columns the API does not carry (product, MBR version, step progress, missing signatures) are not
 * drawn: a number invented to fill a cell would be a claim about the record.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { CreateBatchDialog } from "../components/CreateBatchDialog.js";
import {
  Banner,
  Button,
  type Column,
  DataTable,
  Input,
  Page,
  PageHeader,
  Select,
  StatTile,
  StatusChip,
  type Tone,
} from "../components/ui/index.js";
import { type Batch, commandId, endpoints, refusalMessage } from "../lib/api.js";

/** The batch machine's forward step for a row, with the permission it needs (BATCH in machines.ts). */
const ROW_STEP: Record<string, { event: string; label: string; perm: string }> = {
  CREATED: { event: "ReleaseToProduction", label: "Release to production", perm: "batch.create" },
  RELEASED: { event: "Start", label: "Start batch", perm: "batch.step.perform" },
};

const OPEN = new Set(["CREATED", "RELEASED", "IN_PROGRESS", "ON_HOLD"]);

const STATUS: Record<string, { tone: Tone; icon: string }> = {
  IN_PROGRESS: { tone: "info", icon: "autorenew" },
  ON_HOLD: { tone: "warn", icon: "pause_circle" },
  RELEASED: { tone: "ok", icon: "rocket_launch" },
  CREATED: { tone: "neutral", icon: "pending" },
};

const label = (status: string) => status.replace(/_/g, " ");

/** The forward step (if the user may take it) and the way into the batch. */
function RowActions({
  b,
  perms,
  onRefusal,
}: {
  b: Batch;
  perms: string[];
  onRefusal: (m: string | null) => void;
}) {
  const qc = useQueryClient();
  const step = ROW_STEP[b.status];
  const advance = useMutation({
    mutationFn: () => endpoints.batchTransition(b.id, { clientCommandId: commandId(), event: step?.event }),
    onSuccess: () => {
      onRefusal(null);
      void qc.invalidateQueries({ queryKey: ["batches"] });
    },
    onError: (e: unknown) => onRefusal(`Batch ${b.batch_number}: ${refusalMessage(e)}`),
  });
  return (
    <span className="flex items-center justify-end gap-2">
      {step && perms.includes(step.perm) ? (
        <Button size="floor" loading={advance.isPending} onClick={() => advance.mutate()}>
          {step.label}
        </Button>
      ) : null}
      <Button size="floor" variant="primary" href={`/batches/${b.id}`}>
        Open batch
      </Button>
    </span>
  );
}

function columns(perms: string[], onRefusal: (m: string | null) => void): Column<Batch>[] {
  return [
    {
      key: "number",
      header: "Batch number",
      mono: true,
      sortValue: (b) => b.batch_number,
      cell: (b) => <span className="font-semibold text-ink">{b.batch_number}</span>,
    },
    {
      key: "qty",
      header: "Planned qty",
      align: "right",
      mono: true,
      sortValue: (b) => Number(b.planned_qty),
      cell: (b) => `${b.planned_qty} kg`,
    },
    {
      key: "status",
      header: "Status",
      sortValue: (b) => b.status,
      cell: (b) => {
        const s = STATUS[b.status] ?? (STATUS["CREATED"] as { tone: Tone; icon: string });
        return (
          <StatusChip tone={s.tone} icon={s.icon}>
            {label(b.status).toLowerCase()}
          </StatusChip>
        );
      },
    },
    {
      key: "hold",
      header: "Hold reason",
      cell: (b) => <span className="font-medium text-alarm">{b.hold_reason ?? ""}</span>,
    },
    {
      key: "started",
      header: "Started (server time)",
      mono: true,
      sortValue: (b) => b.started_at_server ?? "",
      cell: (b) => (b.started_at_server ? b.started_at_server.slice(0, 19).replace("T", " ") : "Not started"),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (b) => <RowActions b={b} perms={perms} onRefusal={onRefusal} />,
    },
  ];
}

export function MyWork() {
  const batches = useQuery({
    // ask for the open ones: the newest 200 of every status can all be closed, which hid every open batch
    queryKey: ["batches", "open"],
    queryFn: () => endpoints.batches([...OPEN].join(",")),
    refetchInterval: 30_000,
  });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const me = useQuery({ queryKey: ["me"], queryFn: () => endpoints.me() });
  const perms = me.data?.permissions ?? [];
  const [creating, setCreating] = useState(false);
  const [rowRefusal, setRowRefusal] = useState<string | null>(null);
  const all: Batch[] = batches.data ?? [];
  const open = all.filter((b) => OPEN.has(b.status));
  const shown = open.filter(
    (b) =>
      (status === "ALL" || b.status === status) &&
      b.batch_number.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const count = (s: string) => open.filter((b) => b.status === s).length;
  const loaded = !batches.isPending && !batches.isError;
  return (
    <Page>
      <PageHeader
        title="Batch runs"
        purpose="Every batch open on this line, newest first."
        actions={
          perms.includes("batch.create") ? (
            <Button variant="primary" icon="add" size="floor" onClick={() => setCreating(true)}>
              Create batch from effective MBR
            </Button>
          ) : null
        }
      />
      {creating ? <CreateBatchDialog onClose={() => setCreating(false)} /> : null}
      {rowRefusal ? <Banner tone="alarm">{rowRefusal}</Banner> : null}
      {batches.isPending ? <p role="status">Loading…</p> : null}
      {batches.isError ? (
        <Banner tone="alarm">{refusalMessage(batches.error)} The record is unchanged.</Banner>
      ) : null}
      {loaded ? (
        <>
          <section aria-label="Batch totals" className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <StatTile label="Open batches" value={open.length} />
            <StatTile label="In progress" value={count("IN_PROGRESS")} />
            <StatTile label="On hold" value={count("ON_HOLD")} tone={count("ON_HOLD") ? "warn" : "neutral"} />
            <StatTile label="Released to line" value={count("RELEASED")} />
          </section>
          <div className="flex flex-wrap items-center gap-3">
            <Input
              aria-label="Search by batch number"
              className="max-w-xs font-mono"
              placeholder="Search by batch number"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select
              aria-label="Filter by status"
              className="max-w-xs"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="ALL">{`Status: all open (${open.length})`}</option>
              {[...OPEN].map((s) => (
                <option key={s} value={s}>{`${label(s)} (${count(s)})`}</option>
              ))}
            </Select>
          </div>
          {/*
            Only when the list actually arrived. "No open batches" is a claim about the record; after a failed
            request the system does not know whether it is true, and printing it beneath the failure told the
            operator two contradictory things at once.
          */}
          <DataTable
            caption="Open batches"
            columns={columns(perms, setRowRefusal)}
            rows={shown}
            rowKey={(b) => b.id}
            empty={
              open.length === 0
                ? "No open batches are assigned to this line."
                : "No open batch matches this search."
            }
          />
        </>
      ) : null}
    </Page>
  );
}
